import {
  DndContext,
  DragEndEvent,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  rectSortingStrategy,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import { ReactNode, useEffect, useState } from "react";
import { Flex, Grid } from "styled-system/jsx";
import { sortableRecipe } from "./recipe";

type SortableItemProps = {
  children: ReactNode;
  id: string;
};

const SortableItem = ({ children, id }: SortableItemProps) => {
  const { setNodeRef, attributes, listeners, transform, transition } =
    useSortable({ id: id });

  const style = {
    transition,
    transform: CSS.Transform.toString(transform),
  };

  return (
    <Flex
      key={id}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      flexDir="column"
      alignItems="center"
      justifyContent="flex-end"
      gap="200"
      minW={0}
    >
      {children}
    </Flex>
  );
};

type LocalValues<T> = {
  id: string;
} & Partial<T>;

export type SortableChangeMeta = {
  oldIndex: number;
  newIndex: number;
};

export type SortableProps<T> = {
  disabled: boolean;
  value: Partial<T>[];
  onChange?: (
    value: Partial<T>[],
    meta: SortableChangeMeta,
  ) => Promise<void>;
  sortableComponent?: (
    item: Partial<T> | Omit<LocalValues<T>, "id">,
    index: number,
  ) => ReactNode;
  selectorID: keyof Partial<T>;
  size?: "screen" | "modal" | "compact";
  sortingStrategy?:
    | typeof rectSortingStrategy
    | typeof verticalListSortingStrategy
    | typeof horizontalListSortingStrategy;
};

export const Sortable = <T extends { _id: string }>({
  disabled,
  value = [],
  onChange,
  sortableComponent,
  selectorID = "_id",
  size = "screen",
  sortingStrategy = rectSortingStrategy,
}: SortableProps<T>) => {
  const classes = sortableRecipe({ size });

  /**
   * Set the initial localValues array and update when the incoming values change.
   * localValues adds an `id` property because dnd-kit sorting methods use it.
   *
   * When the parent re-renders with a new `value` array reference that still has
   * the same item ids (common while an async onChange/save is in flight), keep
   * the local drag order and only refresh item payloads. Sync order from props
   * when the id set actually changes (add/remove/initial load).
   */
  const [localValues, setLocalValues] = useState<LocalValues<T>[]>([]);
  useEffect(() => {
    const next = value.map(item => ({
      ...item,
      id: String(item[selectorID]),
    }));
    setLocalValues(prev => {
      if (prev.length === 0) return next;
      const prevIds = prev.map(item => item.id);
      const nextIds = next.map(item => item.id);
      const sameIdSet =
        prevIds.length === nextIds.length &&
        prevIds.every(id => nextIds.includes(id));
      if (!sameIdSet) return next;
      const byId = new Map(next.map(item => [item.id, item]));
      return prevIds.map(id => byId.get(id)!);
    });
  }, [selectorID, value]);

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 10,
    },
  });

  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 250,
      tolerance: 5,
    },
  });

  const sensors = useSensors(mouseSensor, touchSensor);

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (active.id === over?.id) return;
    const oldIndex = localValues.findIndex(item => item._id === active.id);
    const newIndex = localValues.findIndex(item => item._id === over?.id);
    const newArray = arrayMove<LocalValues<T>>(localValues, oldIndex, newIndex);
    setLocalValues(newArray);
    onChange?.(newArray, { oldIndex, newIndex });
  };

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
      sensors={sensors}
      // This prevents the item from being dragged everywhere,
      // which can cause horizontal scrolling, etc.
      modifiers={[restrictToParentElement]}
    >
      <SortableContext
        items={localValues}
        strategy={sortingStrategy}
        disabled={disabled}
      >
        <Grid className={classes}>
          {localValues.map((item, i) => {
            const { id, ...rest } = item;
            return (
              <SortableItem key={id} id={id}>
                {sortableComponent?.(rest, i)}
              </SortableItem>
            );
          })}
        </Grid>
      </SortableContext>
    </DndContext>
  );
};
